import { useState, useCallback } from 'react'
import type { Environment } from '@alpha-edge/shared'
import { PageHeader, DataTable, Modal, ColumnSettingsButton, colors, input, btnSecondary } from '../../components/ui'
import type { Column } from '../../components/ui'
import { useApiList } from '../../hooks/use-api-list'
import { useTableColumns } from '../../hooks/use-table-columns'
import { useAuth } from '../../hooks/use-auth'
import { relativeTime } from '../fleet/fleet-helpers'

// ── styles ────────────────────────────────────────────────────────────────────

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: colors.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  marginBottom: '6px',
}

const chipStyle = (selected: boolean): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '4px 12px',
  borderRadius: '9999px',
  fontSize: '0.8125rem',
  fontWeight: 600,
  cursor: 'pointer',
  border: selected ? `2px solid ${colors.primary}` : `2px solid ${colors.border}`,
  background: selected ? '#eff6ff' : '#fff',
  color: selected ? colors.primary : colors.text,
  userSelect: 'none',
  transition: 'all 0.12s',
})

const chipRemoveStyle: React.CSSProperties = {
  marginLeft: '6px',
  fontSize: '0.75rem',
  cursor: 'pointer',
  color: colors.textMuted,
  lineHeight: 1,
}

const filterRowStyle: React.CSSProperties = {
  marginBottom: '16px',
}

// ── data interface ────────────────────────────────────────────────────────────

interface OperationalEvent {
  readonly id: string
  readonly source: string
  readonly severity: string
  readonly summary: string
  readonly details: Record<string, unknown> | null
  readonly environmentId: string | null
  readonly entityType: string | null
  readonly entityId: string | null
  readonly occurredAt: string
}

// ── constants ─────────────────────────────────────────────────────────────────

const SOURCES = ['', 'provisioning', 'scheduled_job', 'sync_agent', 'backup_agent', 'system'] as const
const SEVERITIES = ['', 'info', 'warn', 'error'] as const

// ── source badge ──────────────────────────────────────────────────────────────

const SOURCE_COLORS: Record<string, { bg: string; text: string }> = {
  provisioning: { bg: '#dbeafe', text: '#1d4ed8' },
  scheduled_job: { bg: '#f3e8ff', text: '#7c3aed' },
  sync_agent: { bg: '#fef9c3', text: '#a16207' },
  backup_agent: { bg: '#d1fae5', text: '#047857' },
  system: { bg: '#f3f4f6', text: '#6b7280' },
}

function SourceBadge({ source }: { readonly source: string }) {
  const c = SOURCE_COLORS[source] ?? { bg: '#f3f4f6', text: '#6b7280' }
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '9999px',
        fontSize: '0.75rem',
        fontWeight: 700,
        background: c.bg,
        color: c.text,
        textTransform: 'capitalize',
        letterSpacing: '0.3px',
      }}
    >
      {source.replace(/_/g, ' ')}
    </span>
  )
}

// ── severity dot ──────────────────────────────────────────────────────────────

const SEVERITY_DOT: Record<string, string> = {
  info: '#22c55e',
  warn: '#eab308',
  error: '#ef4444',
}

function SeverityDot({ severity }: { readonly severity: string }) {
  const color = SEVERITY_DOT[severity] ?? '#9ca3af'
  return (
    <span
      style={{
        display: 'inline-block',
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        background: color,
        marginRight: '6px',
        verticalAlign: 'middle',
      }}
    />
  )
}

// ── detail modal ──────────────────────────────────────────────────────────────

interface EventDetailModalProps {
  readonly row: OperationalEvent | null
  readonly envName: string | null
  readonly onClose: () => void
}

function renderDetailsPanel(details: Record<string, unknown>) {
  const hasTerminal = 'stdout' in details || 'stderr' in details || 'command' in details

  if (!hasTerminal) {
    return (
      <pre
        style={{
          margin: 0,
          fontFamily: 'monospace',
          fontSize: '0.8125rem',
          background: '#1e1e2e',
          color: '#cdd6f4',
          padding: '12px',
          borderRadius: '6px',
          overflowX: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
        }}
      >
        {JSON.stringify(details, null, 2)}
      </pre>
    )
  }

  return (
    <div
      style={{
        fontFamily: 'monospace',
        fontSize: '0.8125rem',
        background: '#1e1e2e',
        color: '#cdd6f4',
        padding: '12px',
        borderRadius: '6px',
        overflowX: 'auto',
      }}
    >
      {'command' in details && <div style={{ marginBottom: '8px', color: '#a6e3a1' }}>$ {String(details.command)}</div>}
      {'stdout' in details && Boolean(details.stdout) && (
        <pre style={{ margin: 0, color: '#cdd6f4', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
          {String(details.stdout)}
        </pre>
      )}
      {'stderr' in details && Boolean(details.stderr) && (
        <pre style={{ margin: '8px 0 0', color: '#f38ba8', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
          {String(details.stderr)}
        </pre>
      )}
    </div>
  )
}

function EventDetailModal({ row, envName, onClose }: EventDetailModalProps) {
  if (!row) return null
  return (
    <Modal isOpen={row !== null} onClose={onClose} title="Event Detail" width="680px">
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '0.875rem',
        }}
      >
        <tbody>
          {(
            [
              ['ID', row.id],
              ['Occurred At', new Date(row.occurredAt).toLocaleString()],
              ['Source', <SourceBadge key="source" source={row.source} />],
              [
                'Severity',
                <span key="sev">
                  <SeverityDot severity={row.severity} />
                  {row.severity}
                </span>,
              ],
              ['Summary', row.summary],
              ['Environment', envName ?? row.environmentId ?? '—'],
              ['Entity Type', row.entityType ?? '—'],
              ['Entity ID', row.entityId ?? '—'],
            ] as Array<[string, React.ReactNode]>
          ).map(([label, value]) => (
            <tr key={label} style={{ borderBottom: `1px solid ${colors.border}` }}>
              <td
                style={{
                  padding: '8px 12px',
                  fontWeight: 600,
                  color: colors.textMuted,
                  width: '130px',
                  verticalAlign: 'top',
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
              </td>
              <td
                style={{
                  padding: '8px 12px',
                  color: colors.text,
                  wordBreak: 'break-all',
                }}
              >
                {value}
              </td>
            </tr>
          ))}
          {row.details && (
            <tr>
              <td
                style={{
                  padding: '8px 12px',
                  fontWeight: 600,
                  color: colors.textMuted,
                  verticalAlign: 'top',
                  whiteSpace: 'nowrap',
                }}
              >
                Details
              </td>
              <td style={{ padding: '8px 12px' }}>{renderDetailsPanel(row.details)}</td>
            </tr>
          )}
        </tbody>
      </table>
    </Modal>
  )
}

// ── component ─────────────────────────────────────────────────────────────────

export function OperationalEventsPage() {
  const { user } = useAuth()
  const isMedflowAdmin = user?.role === 'medflow_admin'

  const [source, setSource] = useState('')
  const [severity, setSeverity] = useState('')
  const [selectedEnvIds, setSelectedEnvIds] = useState<readonly string[]>([])
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [search, setSearch] = useState('')
  const [detailRow, setDetailRow] = useState<OperationalEvent | null>(null)

  const { tableId: _, ...tableColumnProps } = useTableColumns('operational-events')

  // Environment filter passes a single environmentId — only medflow_admin sees it
  const environmentId = isMedflowAdmin && selectedEnvIds.length === 1 ? selectedEnvIds[0] : undefined

  const filters: Record<string, string | undefined> = {
    source: source || undefined,
    severity: severity || undefined,
    environmentId,
    search: search || undefined,
    since: from ? new Date(from).toISOString() : undefined,
    until: to ? new Date(to).toISOString() : undefined,
  }

  const {
    data: rows,
    total,
    loading,
    error,
  } = useApiList<OperationalEvent>({
    path: '/admin/operational-events',
    limit: 50,
    filters,
  })

  const { data: environments, loading: envsLoading } = useApiList<Environment>({
    path: '/fleet/environments',
  })

  const envById = new Map(environments.map((e) => [e.id, e.name]))

  const toggleEnv = useCallback((id: string) => {
    setSelectedEnvIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }, [])

  const hasFilters = selectedEnvIds.length > 0 || source || severity || search || from || to

  const clearFilters = useCallback(() => {
    setSelectedEnvIds([])
    setSource('')
    setSeverity('')
    setSearch('')
    setFrom('')
    setTo('')
  }, [])

  const selectedEnvs = environments.filter((e) => selectedEnvIds.includes(e.id))
  const unselectedEnvs = environments.filter((e) => !selectedEnvIds.includes(e.id))

  const columns: readonly Column<OperationalEvent>[] = [
    {
      key: 'occurredAt',
      header: 'Timestamp',
      render: (r) => relativeTime(r.occurredAt),
      sortable: true,
    },
    {
      key: 'source',
      header: 'Source',
      render: (r) => <SourceBadge source={r.source} />,
      sortable: true,
    },
    {
      key: 'severity',
      header: 'Severity',
      render: (r) => (
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          <SeverityDot severity={r.severity} />
          {r.severity}
        </span>
      ),
      sortable: true,
    },
    {
      key: 'summary',
      header: 'Summary',
      render: (r) => (
        <span title={r.summary} style={{ cursor: 'pointer', color: colors.text }}>
          {r.summary.length > 120 ? r.summary.slice(0, 117) + '...' : r.summary}
        </span>
      ),
    },
    ...(isMedflowAdmin
      ? ([
          {
            key: 'environmentId',
            header: 'Environment',
            render: (r) => (r.environmentId ? (envById.get(r.environmentId) ?? r.environmentId.slice(0, 8)) : '—'),
            sortable: true,
          },
        ] as readonly Column<OperationalEvent>[])
      : []),
    {
      key: 'entityType',
      header: 'Entity',
      render: (r) => r.entityType ?? '—',
      sortable: true,
    },
  ]

  return (
    <div>
      <PageHeader
        title="Operational Events"
        subtitle={`Cross-process system events${total > 0 ? ` — ${total.toLocaleString()} events` : ''}`}
        actions={<ColumnSettingsButton columns={columns} {...tableColumnProps} />}
      />

      <div style={{ padding: '24px' }}>
        {/* Environment filter chips — medflow_admin only */}
        {isMedflowAdmin && (
          <div style={filterRowStyle}>
            <label style={labelStyle}>Filter by Environment (all environments if none selected)</label>
            {selectedEnvs.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
                {selectedEnvs.map((env) => (
                  <span key={env.id} style={chipStyle(true)}>
                    {env.name}
                    <span
                      style={chipRemoveStyle}
                      role="button"
                      onClick={() => toggleEnv(env.id)}
                      title={`Remove ${env.name}`}
                    >
                      x
                    </span>
                  </span>
                ))}
              </div>
            )}
            {envsLoading ? (
              <span style={{ fontSize: '0.8125rem', color: colors.textMuted }}>Loading environments...</span>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {unselectedEnvs.map((env) => (
                  <span
                    key={env.id}
                    style={chipStyle(false)}
                    role="button"
                    onClick={() => toggleEnv(env.id)}
                    title={`Add ${env.name}`}
                  >
                    {env.name}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Source + Severity + Search filters */}
        <div style={{ ...filterRowStyle, display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <div>
            <label style={labelStyle}>Source</label>
            <select value={source} onChange={(e) => setSource(e.target.value)} style={{ ...input, width: '160px' }}>
              {SOURCES.map((s) => (
                <option key={s} value={s}>
                  {s ? s.replace(/_/g, ' ') : '(all)'}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={labelStyle}>Severity</label>
            <select value={severity} onChange={(e) => setSeverity(e.target.value)} style={{ ...input, width: '130px' }}>
              {SEVERITIES.map((s) => (
                <option key={s} value={s}>
                  {s || '(all)'}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={labelStyle}>Search Summary</label>
            <input
              type="text"
              placeholder="keyword in summary"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ ...input, width: '220px' }}
            />
          </div>
        </div>

        {/* Date range */}
        <div style={{ ...filterRowStyle, display: 'flex', gap: '16px' }}>
          <div>
            <label style={labelStyle}>From (event time)</label>
            <input
              type="datetime-local"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              style={{ ...input, width: '200px' }}
            />
          </div>
          <div>
            <label style={labelStyle}>To (event time)</label>
            <input
              type="datetime-local"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              style={{ ...input, width: '200px' }}
            />
          </div>
        </div>

        {/* Filter actions */}
        {hasFilters && (
          <div style={{ ...filterRowStyle, display: 'flex', gap: '12px', alignItems: 'center' }}>
            <button style={btnSecondary} onClick={clearFilters}>
              Clear filters
            </button>
          </div>
        )}

        {/* Error */}
        {error && (
          <div
            style={{
              marginBottom: '16px',
              padding: '12px 16px',
              background: '#fee2e2',
              border: '1px solid #fca5a5',
              borderRadius: '6px',
              color: '#dc2626',
              fontSize: '0.875rem',
            }}
          >
            {error}
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div style={{ color: colors.textMuted, fontSize: '0.875rem' }}>Loading...</div>
        ) : (
          <DataTable
            columns={columns}
            rows={rows}
            keyField="id"
            emptyMessage="No events found for the selected filters."
            onRowClick={(row) => setDetailRow(row)}
            {...tableColumnProps}
          />
        )}
      </div>

      {/* Row detail modal */}
      <EventDetailModal
        row={detailRow}
        envName={detailRow?.environmentId ? (envById.get(detailRow.environmentId) ?? null) : null}
        onClose={() => setDetailRow(null)}
      />
    </div>
  )
}
